import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { AUTH_CONFIG } from '../config';
import { getBaseUrl } from '../shared/utils/apiConfig';
import { authApi, isTwoFactorRequired } from '../shared/services/authApi';

interface User {
  id: string;
  username: string;
  email: string;
  roles: string[];
  tenant_id: string;
  tenant?: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  loginWithToken: (accessToken: string) => Promise<void>;
  logout: () => void;
  // getAuthHeaders removed - use getAuthHeadersSync() from authHelpers instead
  // This prevents React state sync issues with localStorage
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  user: null,
  login: async () => {},
  loginWithToken: async () => {},
  logout: () => {}
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);

  const fetchUserInfo = useCallback(async (authToken: string) => {
    try {
      const response = await fetch(`${getBaseUrl()}/api/auth/verify`, {
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      });
      
      if (response.ok) {
        const userData = await response.json();
        
        // CRITICAL FIX: Map user_id to id for consistency
        // Backend token payload uses 'user_id', but frontend expects 'id'
        if (userData.user_id && !userData.id) {
          userData.id = userData.user_id;
        }
        
        // Enrich user data with username from localStorage if available
        const storedUsername = localStorage.getItem('login_username');
        if (storedUsername && !userData.username) {
          userData.username = storedUsername;
        }
        // Extract tenant from tenant_id if not present
        if (userData.tenant_id && !userData.tenant) {
          userData.tenant = window.location.hostname.split('.')[0].split('-')[0];
        }
        setUser(userData);
        // CRITICAL: Store user in localStorage for RouteGuard to check roles
        localStorage.setItem('user', JSON.stringify(userData));
      } else {
        // If user info fetch fails, log out
        console.warn('User verification failed, clearing auth state');
        localStorage.removeItem(AUTH_CONFIG.tokenStorageKey);
        localStorage.removeItem(AUTH_CONFIG.tokenExpiryKey);
        localStorage.removeItem(AUTH_CONFIG.refreshTokenStorageKey);
        localStorage.removeItem('user'); // CRITICAL: Also remove user data
        setToken(null);
        setIsAuthenticated(false);
        setUser(null);
      }
    } catch (error) {
      console.error('Error fetching user info:', error);
      // Don't logout on network errors, just log the error
    }
  }, []); // Empty deps - function doesn't depend on any state

  useEffect(() => {
    // Check if user is already authenticated
    const storedToken = localStorage.getItem(AUTH_CONFIG.tokenStorageKey);
    const tokenExpiry = localStorage.getItem(AUTH_CONFIG.tokenExpiryKey);
    
    if (storedToken && tokenExpiry) {
      const expiryDate = new Date(tokenExpiry);
      
      if (expiryDate > new Date()) {
        // Token is still valid
        setToken(storedToken);
        setIsAuthenticated(true);
        
        // Fetch user info
        fetchUserInfo(storedToken);
      } else {
        // Token has expired
        logout();
      }
    }
  }, [fetchUserInfo]);

  const login = async (email: string, password: string) => {
    try {
      // SECURITY FIX (Oct 29, 2025): Use authApi.login() which includes tenant_id scope
      // This prevents cross-tenant login vulnerability where users could access wrong tenant
      // 
      // Previous implementation (INSECURE):
      // - Sent OAuth2 request without scope parameter
      // - Backend used user's own tenant_id without validation
      // - Allowed cross-tenant login (SECURITY BUG!)
      //
      // New implementation (SECURE):
      // - authApi.login() sends scope with tenant_id extracted from hostname
      // - Backend validates user's tenant matches requested tenant
      // - Cross-tenant login is BLOCKED
      //
      // See: /brain-board_tests/security/test_tenant_isolation.py
      const response = await authApi.login(email, password);
      
      // Check if 2FA is required (APB-989)
      // If 2FA is required, throw an error - the LoginPage should handle this case
      // AuthContext.login() is only for completing the full login flow
      if (isTwoFactorRequired(response)) {
        throw new Error('2FA_REQUIRED');
      }
      
      // Store access token and expiry (OAuth2 response format)
      localStorage.setItem(AUTH_CONFIG.tokenStorageKey, response.access_token);
      
      // CRITICAL FIX: Store refresh token for automatic token renewal
      // Without this, users get logged out after token expires (30 minutes)
      if (response.refresh_token) {
        localStorage.setItem(AUTH_CONFIG.refreshTokenStorageKey, response.refresh_token);
      } else {
        console.warn('No refresh_token in login response - token refresh will fail after expiration');
      }
      
      // Store username for later enrichment
      localStorage.setItem('login_username', email);
      
      // Use expires_in from OAuth2 response (default to 1800 seconds)
      const expiryDate = new Date();
      const expiresInSeconds = response.expires_in || 1800;
      expiryDate.setSeconds(expiryDate.getSeconds() + expiresInSeconds);
      localStorage.setItem(AUTH_CONFIG.tokenExpiryKey, expiryDate.toISOString());
      
      // Update React state
      setToken(response.access_token);
      setIsAuthenticated(true);
      
      // Fetch user info using the token since OAuth2 response doesn't include user data
      await fetchUserInfo(response.access_token);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const loginWithToken = async (accessToken: string) => {
    localStorage.setItem(AUTH_CONFIG.tokenStorageKey, accessToken);
    setToken(accessToken);
    setIsAuthenticated(true);
    await fetchUserInfo(accessToken);
  };

  const logout = () => {
    localStorage.removeItem(AUTH_CONFIG.tokenStorageKey);
    localStorage.removeItem(AUTH_CONFIG.tokenExpiryKey);
    localStorage.removeItem(AUTH_CONFIG.refreshTokenStorageKey);
    localStorage.removeItem('user'); // CRITICAL: Also remove user data
    localStorage.removeItem('login_username'); // Also remove stored username
    localStorage.removeItem('sessionStartTime'); // Clear absolute timeout anchor so next login starts fresh
    
    setToken(null);
    setIsAuthenticated(false);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, login, loginWithToken, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
